import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { jsonStore } from "@/manager/cache/localdb";
import { getAgentMessages, MessageItemData } from "@/services/push-info-origin-data-record-list";
import { generateMessage } from "@/services/make-generate";
import type { ScriptReplaceMark } from "@/services/make-generate";
import { getGenerateMessageResult, GenerateMessageResult } from "@/services/make-get-task-status";
import { subscriptionManager } from "./subscription-manager";
import { useParams } from "react-router-dom";
import { pushInfoOriginDataRecordDelete } from "@/services/push-info-origin-data-record-delete";
import { useTranslation } from "react-i18next";
import { forceHttps } from "@/utils/force-https";
import { cancelTaskCancel } from "@/services/cancel-task-cancel";
import { generateSora2TextToVod } from "@/services/sj-sora2-txt2vod-create";
import { cosUploadFile } from "@/services/cos-upload-file";
import { generateSora2ImageToVod } from "@/services/sj-sora2-img2vod-create";
import { generateGeminiImage } from "@/services/sj-gemini-image-generate-create";
import { CREATION_DEFAULT_CONFIG, MODEL_PARAMS_CONFIGS } from "@/components/agent-creation/constants/ai-creation-parameter";
import { AiMakeMessageRecordData, aiMakeSourceRecordListByType } from "@/services/ai-make-source-record-list-by-type";
import { deleteMakeSourceRecord } from "@/services/ai-make-source-record-delete";
import { getModelOptionByValue, KLING_V26_PRO, NANO_BANANA, NANO_BANANA_PRO, SEEDREAM_V45, SORA2, SORA2_PRO } from "@/components/agent-creation/constants/ai-creation-model";
import { generateSeedreamImage } from "@/services/sj-seedream-image-generate-create";
import { generateKlingTextToVod } from "@/services/sj-kling-txt2vod-create";
import { generateKlingImageToVod } from "@/services/sj-kling-img2vod-create";
import { PollingManager } from "./polling-manager";
import { audioContentIllegal } from "@/services/audio-content-illegal";
import { audioTTSCreate, AudioTTSCreateParams, AudioTTSCreateProps } from "@/services/audio-tts-create";
import { audioTTSResult } from "@/services/audio-tts-result";
import { pollUntilCompleted } from "@/utils/pollUntilTaskCompleted";
import type { EstimatedDurationState } from "@/components/agent-creation/estimated-video-duration";
import { useCachedState } from "@/hooks/use-cached-state";
import { agentManager } from "./agent-manager";

/** 思考任务状态 */
export const TASK_STATUS = {
  /** 排队中 */
  QUEUED: "queued",
  /** 待处理 */
  PENDING: "pending",
  /** 思考中 */
  THINKING: "thinking",
  /** 生成中 */
  PROCESSING: "processing",
  /** 超时 */
  PROCESS_TIMEOUT: "process_timeout",
  /** 成功(不取这里) */
  SUCCESS: "success",
  /** 失败(不取这里) */
  FAILED: "failed",
  /** 未知状态 */
  COMPLETED: "completed",
} as const;

/** 输入类型 */
export const INPUT_TYPE = {
  /** 短视频 */
  VIDEO: 1,
  /** 播客 */
  PODCAST: 2,
  /** 朋友圈 */
  MOMENTS: 3,
  /** 图文 */
  POST: 4,
  /** 文章 */
  ARTICLE: 5,
  /** Ai创作 */
  AI_CREATION: 6,
} as const;

// ai创作类型
export type AiCreationType = 'video' | 'image';
// ai创作参数
export interface AiCreationParams {
  // 比例
  aspectRatio?: string;
  // 分辨率
  imageSize?: string;
  // 时长
  duration?: string;
  // 输出数量
  count?: string;
}

export interface TTSCreateResultData {
  /** 任务ID（用于轮询） */
  taskId: string;
  /** 任务状态 */
  status: "processing" | "completed" | "failed";
  /** 音频url */
  fileUrl: string;
}

export type { ScriptReplaceMark } from "@/services/make-generate";

// 视频生成成函数映射表
const videoModelRunnerMap = {
  [SORA2]: {
    text: generateSora2TextToVod,
    image: generateSora2ImageToVod,
  },
  [SORA2_PRO]: {
    text: generateSora2TextToVod,
    image: generateSora2ImageToVod,
  },
  [KLING_V26_PRO]: {
    text: generateKlingTextToVod,
    image: generateKlingImageToVod,
  },
};

// 图片生成成函数映射表
const imageModelRunnerMap = {
  [NANO_BANANA]: generateGeminiImage,
  [NANO_BANANA_PRO]: generateGeminiImage,
  [SEEDREAM_V45]: generateSeedreamImage,
};

export type AgentUserMessageType = "video" | "podcast" | "moments" | "post" | "article" | "ai";

export type AgentUserMessageItem = MessageItemData;

type ChangePayload = {
  agentId?: string;
  items: AgentUserMessageItem[];
};

type MessageEvent = CustomEvent<ChangePayload>;

type AgentUserMessagesEventMap = {
  messages: ChangePayload;
  tts: TTSCreateResultData | null;
};

class AgentUserMessagesEvents {
  constructor(private eventName: string) { }
  private getEventKey<K extends keyof AgentUserMessagesEventMap>(key: K): string {
    return `${this.eventName}:${String(key)}`;
  }
  listen<K extends keyof AgentUserMessagesEventMap>(
    key: K,
    listener: (e: CustomEvent<AgentUserMessagesEventMap[K]>) => void
  ): () => void {
    const eventKey = this.getEventKey(key);
    window.addEventListener(eventKey, listener as EventListener);
    return () => {
      window.removeEventListener(eventKey, listener as EventListener);
    };
  }
  dispatch<K extends keyof AgentUserMessagesEventMap>(
    key: K,
    detail: AgentUserMessagesEventMap[K]
  ): void {
    const eventKey = this.getEventKey(key);
    const event = new CustomEvent<AgentUserMessagesEventMap[K]>(eventKey, { detail });
    window.dispatchEvent(event);
  }
}

class AgentUserMessagesLocal {
  constructor(private storageKey: string) { }
  async get(): Promise<AgentUserMessageItem[] | null> {
    return await jsonStore.get<AgentUserMessageItem[]>(this.storageKey);
  }
  async set(items: AgentUserMessageItem[]): Promise<void> {
    await jsonStore.set(this.storageKey, items);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AgentUserMessagesRemote {
  constructor(private cache: AgentUserMessagesLocal, private agentId: string, private type?: "" | "1" | "2" | "3" | "4" | "5" | "6") { }
  async getCached() { return await this.cache.get(); }
  async fetch(page: number, pageSize: number): Promise<AgentUserMessageItem[]> {
    const items = await getAgentMessages({ agentId: this.agentId, page, pageSize, type: this.type });
    await this.cache.set(items as AgentUserMessageItem[]);
    return items as AgentUserMessageItem[];
  }
  // 删除消息
  async deleteMessage(id: number) {
    return await pushInfoOriginDataRecordDelete(id);
  }
  // 取消生成
  async cancelGenerate(taskId: string, type: number) {
    return await cancelTaskCancel(taskId, type);
  }
  // 获取创作记录
  async getCreationRecords({
    type,
    page,
    pageSize,
  }: {
    type: number;
    page: number;
    pageSize: number;
  }) {
    return await aiMakeSourceRecordListByType({
      agentId: this.agentId,
      type,
      page,
      pageSize
    });
  }
  // 删除创作记录的某条记录
  async deleteCreationOneofRecord(ids: number[]) {
    return await deleteMakeSourceRecord(ids);
  }
}

export class AgentUserMessagesManager {
  // private static agentGeneratingLocks = new Set<string>();
  private readonly GENERATE_TASK_LIMIT = 5; // 生成任务最大数量
  private readonly STORAGE_PREFIX = "MIUTA_AGENT_USER_MESSAGES";
  private agentId?: string;
  private page: number = 1;
  private pageSize: number = 20;
  private type?: AgentUserMessageType | "";
  private items: AgentUserMessageItem[] = []; // 智能体消息
  private hasMore: boolean = true;
  // 音频预览数据
  private ttsCreateResult: TTSCreateResultData | null = {} as TTSCreateResultData;

  // 轮询管理器，用于管理所有生成任务的轮询状态
  private pollingManager: PollingManager;

  private cache: AgentUserMessagesLocal;
  private api: AgentUserMessagesRemote;
  private events: AgentUserMessagesEvents;

  constructor(agentId?: string, type?: AgentUserMessageType | "") {
    this.agentId = agentId;
    this.type = type;
    const typeParam = this.type ? ({ video: "1", podcast: "2", moments: "3", post: "4", article: "5", ai: "6" } as const)[this.type] : "";
    const key = `${this.STORAGE_PREFIX}:${this.agentId ?? "default"}:${this.type ?? "all"}:${this.page}:${this.pageSize}`;
    this.cache = new AgentUserMessagesLocal(key);
    this.api = new AgentUserMessagesRemote(this.cache, this.agentId || "", typeParam);
    this.events = new AgentUserMessagesEvents(key);
    // 初始化轮询管理器
    this.pollingManager = new PollingManager();
  }

  public async init(): Promise<void> {
    const prevPage = this.page;
    this.page = 1;
    try {
      await this.fetch();
    } catch {
      this.page = prevPage;
      await this.fetch();
    }
  }

  public getItems(): AgentUserMessageItem[] { return this.items; }
  public setItems(items: AgentUserMessageItem[]) { this.items = items; this.dispatch(); }
  public listen(listener: (e: MessageEvent) => void): () => void { return this.events.listen("messages", listener); }

  public getTTSCreateResult() { return this.ttsCreateResult; }
  public ttsListen(listener: (e: CustomEvent<TTSCreateResultData | null>) => void): () => void { return this.events.listen("tts", listener); }

  private async fetch(): Promise<void> {
    const items = await this.api.fetch(this.page, this.pageSize);
    this.hasMore = items.length === this.pageSize;
    const normalizedItems = this.normalizeItems(items);
    this.setItems(normalizedItems);
    this.startPollingForExistingGeneratingTasks();
  }

  public async loadMore(): Promise<void> {
    if (!this.hasMore) return;
    const prevPage = this.page;
    const prevItems = this.items ? [...this.items] : [];
    try {
      this.page = prevPage + 1;
      const nextItems = await this.api.fetch(this.page, this.pageSize);
      this.hasMore = nextItems.length === this.pageSize;
      const normalizedItems = this.normalizeItems(nextItems);
      this.setItems(prevItems.concat(normalizedItems));
      this.startPollingForExistingGeneratingTasks();
    } catch (error) {
      this.page = prevPage;
      console.error(error);
      throw error;
    }
  }

  // 统一的数据规范化逻辑
  private normalizeItems(items: MessageItemData[]): AgentUserMessageItem[] {
    return items.map((item) => {
      const typeValue = typeof item.type === "number" ? item.type : parseInt(String(item.type || "0"), 10) || 0;
      const statusValue = typeof item.status === "number" ? item.status : parseInt(String(item.status || "0"), 10) || 0;
      const videoUrl = String(item.s3VideoUrl || forceHttps(item.videoUrl) || "").trim();
      const audioUrl = String(item.s3AudioUrl || forceHttps(item.audioUrl) || "").trim();
      const content = String(item.content || "").trim();
      const imageUrls = String(item.s3ImageUrls || item.imageUrls || "").trim();

      const isAbnormal = ([1].includes(typeValue) && videoUrl === "")
        || ([2].includes(typeValue) && audioUrl === "")
        || ([4].includes(typeValue) && imageUrls === "")
        || ([3, 5].includes(typeValue) && content === "");

      let finalStatus = statusValue;
      if (statusValue === 1 && isAbnormal) finalStatus = 2;

      return {
        ...item,
        type: typeValue,
        status: finalStatus,
        videoUrl,
        audioUrl,
        imageUrls: imageUrls?.split(",")?.map((url) => forceHttps(url)).join(","),
        coverImageUrl: item.s3CoverImageUrl || forceHttps(item.coverImageUrl),
      };
    }) as AgentUserMessageItem[];
  }

  // 获取当前正在生成的任务数量
  private getGeneratingCount(): number {
    return this.pollingManager.getPollingCount();
  }

  // 开始轮询已存在的生成任务
  private startPollingForExistingGeneratingTasks() {
    this.items.forEach(message => {
      const taskId = String(message.taskId || "").trim();
      const status = typeof message.status === "number"
        ? message.status
        : parseInt(String(message.status || "0"), 10) || 0;

      // status === 0 且不在轮询中 且不是本地占位符
      if (status === 0 && taskId && !taskId.startsWith("local_") && !this.pollingManager.isPolling(taskId)) {
        this.startQueryGenerateMessageResult(taskId);
      }
    });
  }

  // 开始轮询生成任务结果
  private async startQueryGenerateMessageResult(taskId: string) {
    if (!taskId) return;

    // 检查是否已在轮询中，防止重复
    if (this.pollingManager.isPolling(taskId)) {
      return;
    }

    await this.pollingManager.start({
      taskId,
      interval: 5000, // 轮询间隔5秒
      maxRetries: -1, // 无限重试（可根据需要调整）
      pollFn: async () => {
        try {
          const result = await getGenerateMessageResult({ taskId });
          this.upsertMessage(result);

          // 任务结束时停止轮询
          if (result.status !== 0) {
            this.pollingManager.stop(taskId);
            this.cleanup(taskId);
          }
        } catch (error) {
          console.error("Polling error:", error);
          throw error; // 让 PollingManager 处理重试
        }
      },
    });
  }

  // 清理生成任务结果
  private cleanup(taskId: string) {
    this.pollingManager.stop(taskId);
    subscriptionManager.fetchAgentCredits(this.agentId);
  }

  private upsertMessage(entity: GenerateMessageResult) {
    const item: AgentUserMessageItem = {
      id: entity.id,
      type: (typeof entity.type === "number" ? entity.type : parseInt(String(entity.type || "0"), 10) || 0),
      title: entity.title,
      content: entity.content,
      inputText: entity.inputText,
      coverImageUrl: entity.s3CoverImageUrl || forceHttps(entity.coverImageUrl || entity.thumbnail || ''),
      videoUrl: entity.s3VideoUrl || forceHttps(entity.videoUrl),
      imageUrls: entity?.imageUrls?.map((url: string) => forceHttps(url)).join(","),
      audioUrl: entity.s3AudioUrl || forceHttps(entity.audioUrl),
      createdAt: entity.createdAt,
      taskId: entity.taskId,
      status: entity.status,
      videoDuration: entity.videoDuration,
      audioDuration: entity.audioDuration,
      summary: entity.summary,
      reqBody: entity.reqBody,
      noteContentTitle: entity.noteContentTitle || (entity.reqBody ? JSON.parse(entity.reqBody)?.noteContentTitle : ""),
      thinkingList: entity.thinkingList || [],
      taskStatus: entity.taskStatus || "",
      s3CoverImageUrl: entity.s3CoverImageUrl || "",
      s3VideoUrl: entity.s3VideoUrl || "",
      s3AudioUrl: entity.s3AudioUrl || "",
      linkUrl: entity.linkUrl || "",
      n8nCode: entity.n8nCode || "",
      n8nMessage: entity.n8nMessage || "",
      autoPush: !entity.inputText && !entity.linkUrl && !entity.noteContentTitle,
      errMessage: entity.errMessage || "",
      inputAddress: entity.inputAddress || "",
      aiMakeSourceRecords: entity.aiMakeSourceRecords || [],
    };

    const typeValue = item.type;
    const videoUrl = String(item.s3VideoUrl || forceHttps(item.videoUrl) || "").trim();
    const audioUrl = String(item.s3AudioUrl || forceHttps(item.audioUrl) || "").trim();
    const content = String(item.content || "").trim();
    const imageUrls = String(item.s3ImageUrls || item.imageUrls || "").trim();

    const isAbnormal = ([1].includes(typeValue) && videoUrl === "")
      || ([2].includes(typeValue) && audioUrl === "")
      || ([4].includes(typeValue) && imageUrls === "")
      || ([3, 5].includes(typeValue) && content === "");

    const isOverOneDay = item.createdAt && (Date.now() - item.createdAt * 1000 > 24 * 60 * 60 * 1000);
    if ((entity.status === 1 && isAbnormal) || isOverOneDay) item.status = 2;

    const byTaskIndex = this.items.findIndex((m) => String(m.taskId || "") === entity.taskId);
    if (byTaskIndex >= 0) {
      this.items[byTaskIndex] = item;
    } else {
      const byIdIndex = this.items.findIndex((m) => m.id === entity.id);
      if (byIdIndex >= 0) this.items[byIdIndex] = item; else this.items = [item, ...this.items];
    }
    this.setItems([...this.items]);
  }

  public async generateMessage(params: {
    text: string;
    type: number;
    templateId?: number;
    voiceId?: string;
    linkUrl?: string;
    noteId?: number;
    noteName?: string;
    templateResource?: number;
    outLang?: string;
    typeScene?: string;
    marks?: ScriptReplaceMark[];
    point?: number;
    resultVideoDuration?: number;
  }) {
    const aid = this.agentId || "";
    if (!aid) return;

    // 检查并发任务限制
    if (this.getGeneratingLock()) {
      throw new Error(`Generation limit reached (${this.GENERATE_TASK_LIMIT})`);
    }

    const now = Date.now();
    const placeholderTaskId = `local_${now}`;
    const placeholder: AgentUserMessageItem = {
      id: null,
      type: params.type,
      title: "",
      content: "",
      inputText: params.text,
      coverImageUrl: "",
      videoUrl: "",
      imageUrls: "",
      audioUrl: "",
      createdAt: Math.floor(now / 1000),
      taskId: placeholderTaskId,
      status: 0,
      videoDuration: 0,
      audioDuration: 0,
      linkUrl: params.linkUrl,
      noteContentTitle: params.noteName,
      taskStatus: "",
    };

    console.log('生成消息：', {
      agentId: aid,
      text: params.text,
      type: params.type as 1 | 2 | 3 | 4 | 5,
      templateId: params.templateId,
      voiceId: params.voiceId,
      linkUrl: params.linkUrl,
      noteId: params.noteId,
      templateResource: params.templateResource,
      outLang: params.outLang,
      typeScene: params.typeScene,
      marks: params.marks,
      resultVideoDuration: params.resultVideoDuration,
    }, { placeholder })

    this.items = [placeholder, ...(this.items || [])];
    this.dispatch();

    try {
      const taskId = await generateMessage({
        agentId: aid,
        text: params.text,
        type: params.type as 1 | 2 | 3 | 4 | 5,
        templateId: params.templateId,
        voiceId: params.voiceId,
        linkUrl: params.linkUrl,
        noteId: params.noteId,
        templateResource: params.templateResource,
        outLang: params.outLang,
        typeScene: params.typeScene,
        marks: params.marks,
        point: params.point,
        resultVideoDuration: params.resultVideoDuration,
      });

      const idx = this.items.findIndex((m) => String(m.taskId || "") === placeholderTaskId);
      if (idx >= 0) {
        this.items[idx].taskId = taskId;
        this.dispatch();
      }
      subscriptionManager.fetchAgentCredits(aid);
      this.startQueryGenerateMessageResult(taskId);
    } catch (err) {
      this.items = this.items.filter((m) => String(m.taskId || "") !== placeholderTaskId);
      this.dispatch();
      throw err;
    }
  }

  // 生成音频预览
  public async generateTTSAudioPreview(params: AudioTTSCreateProps): Promise<TTSCreateResultData> {
    const createData = await audioTTSCreate({
      ...params,
      marks: params.marks || [],
    });

    this.ttsCreateResult = {
      taskId: createData.taskId,
      status: createData.status,
      fileUrl: "",
    };
    this.dispatchTTS();

    if (createData.status === "failed") {
      throw new Error("Audio TTS create failed");
    }

    const completedData = await pollUntilCompleted<TTSCreateResultData | null>(
      async () => {
        const resultData = await audioTTSResult({ taskId: createData.taskId });
        if (resultData.status === "failed") {
          throw new Error("Audio TTS generate failed");
        }
        if (resultData.status !== "completed") {
          return null;
        }

        return {
          taskId: createData.taskId,
          status: resultData.status,
          fileUrl: resultData.fileUrl || "",
        };
      },
      { delay: 3000, retry: 0 }
    );

    if (!completedData) {
      throw new Error("Audio TTS generate timeout");
    }

    this.ttsCreateResult = completedData;
    this.dispatchTTS();
    return completedData;
  }

  // 生成 Sora2 视频
  public async generateAiCreationVideo(params: {
    text: string;
    type: number;
    model: string;
    duration: number;
    aspectRatio: string;
    imageSize?: string;
    level?: 'ultra' | 'high' | 'low';
    fileUrl?: string;
    fileUrl1?: string;
  }) {
    const aid = this.agentId || "";
    if (!aid || this.getGeneratingLock()) return;

    const modelOption = getModelOptionByValue(params.model);
    const runner = videoModelRunnerMap[modelOption?.value];
    if (!runner) throw new Error(`Model ${params.model} is not supported`);

    const now = Date.now();
    const placeholderTaskId = `local_${now}`;
    const placeholder: AgentUserMessageItem = {
      id: null,
      type: params.type,
      title: "",
      content: "",
      inputText: params.text,
      coverImageUrl: "",
      videoUrl: "",
      imageUrls: "",
      audioUrl: "",
      createdAt: Math.floor(now / 1000),
      taskId: placeholderTaskId,
      status: 0,
      videoDuration: 0,
      audioDuration: 0,
      linkUrl: '',
      noteContentTitle: '',
      taskStatus: "",
      inputAddress: params.fileUrl || '',
      reqBody: JSON.stringify(params)
    };

    this.items = [placeholder, ...(this.items || [])];
    this.dispatch();

    try {
      const requestFn = params.fileUrl ? runner.image : runner.text;
      const taskId = await requestFn({
        ...params,
        agentId: aid,
        level: params.level || 'high',
      });

      const idx = this.items.findIndex((m) => String(m.taskId || "") === placeholderTaskId);
      if (idx >= 0) {
        this.items[idx].taskId = taskId;
        this.dispatch();
      }
      subscriptionManager.fetchAgentCredits(aid);
      this.startQueryGenerateMessageResult(taskId);
    } catch (err) {
      this.items = this.items.filter((m) => String(m.taskId || "") !== placeholderTaskId);
      this.dispatch();
      throw err;
    }
  }

  // 生成 Gemini/Seedream 图片
  public async generateAiCreationImage(params: {
    fileUrls?: string[];
    text: string;
    level?: 'ultra' | 'high' | 'low';
    modelId: string;
    aspectRatio: string;
    imageSize: string;
    count: number;
    type: number;
  }) {
    const aid = this.agentId || "";
    if (!aid || this.getGeneratingLock()) return;

    const modelConfig = MODEL_PARAMS_CONFIGS[params.modelId];
    const isNeedImageSize = modelConfig?.fields.includes('imageSize');
    const modelRunner = imageModelRunnerMap[params.modelId];
    if (!modelRunner) throw new Error(`Model ${params.modelId} is not supported`);

    const now = Date.now();
    const placeholderTaskId = `local_${now}`;
    const placeholder: AgentUserMessageItem = {
      id: null,
      type: params.type,
      title: "",
      content: "",
      inputText: params.text,
      coverImageUrl: "",
      videoUrl: "",
      imageUrls: "",
      audioUrl: "",
      createdAt: Math.floor(now / 1000),
      taskId: placeholderTaskId,
      status: 0,
      videoDuration: 0,
      audioDuration: 0,
      linkUrl: '',
      noteContentTitle: '',
      taskStatus: "",
      inputAddress: params.fileUrls?.join(',') || '',
      reqBody: JSON.stringify(params)
    };

    this.items = [placeholder, ...(this.items || [])];
    this.dispatch();

    try {
      const taskId = await modelRunner({
        ...params,
        imageSize: isNeedImageSize ? params.imageSize : undefined,
        agentId: aid,
        level: params.level || 'high',
      });

      const idx = this.items.findIndex((m) => String(m.taskId || "") === placeholderTaskId);
      if (idx >= 0) {
        this.items[idx].taskId = taskId;
        this.dispatch();
      }
      subscriptionManager.fetchAgentCredits(aid);
      this.startQueryGenerateMessageResult(taskId);
    } catch (err) {
      this.items = this.items.filter((m) => String(m.taskId || "") !== placeholderTaskId);
      this.dispatch();
      throw err;
    }
  }

  // 获取创作记录
  public async getAiCreationRecords(type: number, page: number, pageSize: number) {
    try {
      return await this.api.getCreationRecords({ type, page, pageSize });
    } catch (err) {
      console.error("getAiCreationRecords failed:", err.message);
      throw err;
    }
  }

  // 删除消息
  public async deleteMessage(id: number) {
    try {
      return await this.api.deleteMessage(id);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  public async cancelGenerate(taskId: string, type: number) {
    try {
      await this.api.cancelGenerate(taskId, type);
      this.pollingManager.stop(taskId); // 取消时立即停止本地轮询
      subscriptionManager.fetchAgentCredits(this.agentId); // 获取积分更新
      await this.fetch();
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  public async deleteCreationOneofRecord(ids: number[]) {
    try {
      return await this.api.deleteCreationOneofRecord(ids);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  public reset(): void {
    this.stopAllPolling();
    this.items = [];
    this.clear();
  }

  public clear(): void {
    this.stopAllPolling();
    this.cache.clear();
  }

  public stopAllPolling() {
    console.log('停止所有轮询', this.pollingManager.getActiveTaskIds());
    this.pollingManager.stopAll();
  }

  private dispatch(): void { this.events.dispatch("messages", { agentId: this.agentId, items: this.items }); }
  private dispatchTTS(): void { this.events.dispatch("tts", this.ttsCreateResult); }

  public getGeneratingLock(): boolean {
    // 检查是否超过最大并发任务数
    console.log('Check generating lock:', { generatingCount: this.getGeneratingCount() })
    return this.getGeneratingCount() >= this.GENERATE_TASK_LIMIT;
  }

  public setTTSCreateResult(result: TTSCreateResultData) {
    this.ttsCreateResult = result;
    this.dispatchTTS();
  }
}

export type GroupRecord = {
  type: number;
  date: string;
  time: string;
  messages: AgentUserMessageItem[];
};

const toDateTime = (tsMs: number): { date: string; time: string } => {
  const d = new Date(tsMs);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const HH = String(d.getHours()).padStart(2, "0");
  const MM = String(d.getMinutes()).padStart(2, "0");
  const SS = String(d.getSeconds()).padStart(2, "0");
  return { date: `${yyyy}-${mm}-${dd}`, time: `${HH}:${MM}:${SS}` };
};

const groupByTypeAndTime = (items: AgentUserMessageItem[]): GroupRecord[] => {
  const groups: GroupRecord[] = [];
  const indexMap = new Map<string, number>();

  // 去重
  const uniqueItems = Array.from(
    new Map(items.map(item => [item.taskId, item])).values()
  );

  for (const it of uniqueItems) {
    const rawTs = it.createdAt;
    let ts = 0;
    if (typeof rawTs === "number") ts = rawTs;
    else if (typeof rawTs === "string") ts = parseInt(rawTs, 10) || 0;
    const tsMs = ts > 1e12 ? ts : ts * 1000;
    const { date, time } = toDateTime(tsMs);
    const tnum = typeof it.type === "number" ? (it.type as number) : parseInt(String(it.type), 10) || 0;
    const statusRaw = it.status;
    const status = typeof statusRaw === "number" ? statusRaw : parseInt(String(statusRaw || "0"), 10) || 0;

    if (status !== 1) {
      groups.push({ type: tnum, date, time, messages: [it] });
      continue;
    }

    const key = `${tnum}|${date}|${time}|${status}`;
    const idx = indexMap.get(key);
    if (idx === undefined) {
      indexMap.set(key, groups.length);
      groups.push({ type: tnum, date, time, messages: [it] });
    } else {
      groups[idx].messages.push(it);
    }
  }
  return groups.sort((a, b) => {
    const at = new Date(`${a.date} ${a.time}`).getTime();
    const bt = new Date(`${b.date} ${b.time}`).getTime();
    return bt - at;
  });
};

const useAgentUserMessages = ({
  agentId,
  type = "",
}: { agentId: string; type?: AgentUserMessageType | "" }) => {
  const params = useParams<{ id: string }>();
  const { t } = useTranslation();
  const managerRef = useRef<AgentUserMessagesManager | null>(null);
  const [groups, setGroups] = useState<GroupRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const { showToastByCode, toast } = useToast();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteCreationOneofRecordLoading, setDeleteCreationOneofRecordLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (loadingMore) return;
    if (loadingRefresh) return;
    managerRef.current = new AgentUserMessagesManager(agentId, type);
    // 先订阅后初始化，确保能及时收到更新
    const unsubscribe = managerRef.current.listen((e) => setGroups(groupByTypeAndTime(e.detail.items)));
    setLoading(true);
    managerRef.current
      .init()
      .catch((err) => showToastByCode(err))
      .finally(() => setLoading(false));
    return () => {
      unsubscribe();
      managerRef.current.stopAllPolling();
    };
  }, [agentId, type]);

  const loadMore = async () => {
    if (loading) return;
    if (loadingMore) return;
    if (loadingRefresh) return;
    setLoadingMore(true);
    try {
      await managerRef.current?.loadMore();
    } catch (err) {
      showToastByCode(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const refresh = async () => {
    if (loading) return;
    if (loadingMore) return;
    if (loadingRefresh) return;
    setLoadingRefresh(true);
    try {
      await managerRef.current?.init();
    } catch (err) {
      showToastByCode(err);
    } finally {
      setLoadingRefresh(false);
    }
  };

  const generate = async (args: {
    text: string;
    type: number;
    templateId?: number;
    voiceId?: string;
    linkUrl?: string;
    noteId?: number;
    noteName?: string;
    templateResource?: number;
    outLang?: string;
    typeScene?: string;
    marks?: ScriptReplaceMark[];
    point?: number;
    resultVideoDuration?: number;
  }) => {
    try {
      if (managerRef.current.getGeneratingLock()) {
        toast({
          description: t('agent_creation.content.has_generating')
        })
        return;
      }
      await managerRef.current?.generateMessage(args);
    } catch (err) {
      showToastByCode(err);
      throw err;
    }
  };

  // 视频创作
  const generateAiCreationVideo = async (args: {
    text: string;
    type: number;
    model: string,
    duration: number,
    aspectRatio: string,
    imageSize?: string,
    fileUrl?: string,
    fileUrl1?: string,
  }) => {
    try {
      if (managerRef.current.getGeneratingLock()) {
        toast({
          description: t('agent_creation.content.has_generating')
        })
        return;
      }
      await managerRef.current?.generateAiCreationVideo({
        ...args,
        model: args.model,
        duration: Number(args.duration),
        aspectRatio: args.aspectRatio,
      });
    } catch (err) {
      showToastByCode(err);
      throw err;
    }
  };

  // 图片创作
  const generateAiCreationImage = async (args: {
    text: string;
    type: number;
    model: string,
    imageSize: string,
    count?: number,
    aspectRatio?: string,
    fileUrls?: string[],
  }) => {
    try {
      if (managerRef.current.getGeneratingLock()) {
        toast({
          description: t('agent_creation.content.has_generating')
        })
        return;
      }
      await managerRef.current?.generateAiCreationImage({
        ...args,
        modelId: args.model,
        imageSize: args.imageSize,
        count: Number(args.count),
        aspectRatio: args.aspectRatio,
      });
    } catch (err) {
      showToastByCode(err);
      throw err;
    }
  };

  // 删除消息
  const deleteMessage = async (id: number | number[]) => {
    setDeleteLoading(true);
    try {
      if (Array.isArray(id)) {
        await Promise.all(id.map((i) => managerRef.current?.deleteMessage(Number(i))));
      } else {
        await managerRef.current?.deleteMessage(Number(id));
      }
      // 删除成功后,过滤掉groups中,message的id等于id的项
      if (Array.isArray(id)) {
        id.forEach((i) => removeMessageById(Number(i)));
      } else {
        removeMessageById(Number(id));
      }
      toast({
        description: t('agent_creation.content.deleted')
      })
    } catch (err) {
      console.log(err)
      showToastByCode(err);
    } finally {
      setDeleteLoading(false);
    }
  }

  // 删除创作记录的某条记录
  const deleteCreationOneofRecord = async (params: { ids: number | number[]; taskId: string }) => {
    setDeleteCreationOneofRecordLoading(true);
    try {
      const idArr = Array.isArray(params.ids) ? params.ids : [params.ids];
      await managerRef.current?.deleteCreationOneofRecord(idArr);
      // 删除成功后,过滤掉items中,ai_make_source_records的id等于id的项
      idArr.forEach((id) => removeAiMakeSourceRecordById(params.taskId, id));
      toast({
        description: t('agent_creation.content.deleted')
      })
    } catch (err) {
      console.log(err)
      showToastByCode(err);
    } finally {
      setDeleteCreationOneofRecordLoading(false);
    }
  }

  // 从items中删除指定id的消息
  const removeMessageById = (id: number) => {
    if (!managerRef.current.getItems().find((m) => m.id === id)) return;
    managerRef.current.setItems(managerRef.current.getItems().filter((m) => m.id !== id));
  }

  // 从 items 中删除指定 taskId 的消息中的 ai_make_source_records 的指定id项
  // 如果删除的是ai_make_source_records最后一项，也要将这条 taskId 对应的消息从 items 中删除
  const removeAiMakeSourceRecordById = (taskId: string, id: number) => {
    const currentItems = managerRef.current.getItems();

    // 找到目标消息
    if (!currentItems.find((m) => m.taskId === taskId)) return;

    const nextItems = currentItems
      .map((m) => {
        if (m.taskId === taskId) {
          // 过滤掉指定的 record
          const updatedRecords = m.aiMakeSourceRecords?.filter((r) => r.id !== id) || [];
          return { ...m, aiMakeSourceRecords: updatedRecords };
        }
        return m;
      })
      .filter((m) => {
        // 关键逻辑：如果是当前操作的 taskId，且过滤后 records 为空，则从 items 中剔除
        if (m.taskId === taskId) {
          return m.aiMakeSourceRecords && m.aiMakeSourceRecords.length > 0;
        }
        return true;
      });

    managerRef.current.setItems(nextItems);
  };

  // 取消生成
  const cancelGenerate = async (taskId: string, type: number) => {
    try {
      setCanceling(true);
      await managerRef.current?.cancelGenerate(taskId, type);
    } catch (err) {
      showToastByCode(err);
    } finally {
      setCanceling(false);
    }
  }

  return {
    groups,
    loading,
    loadingMore,
    loadingRefresh,
    loadMore,
    refresh,
    generate,
    deleteMessage,
    deleteLoading,
    removeMessageById,
    cancelGenerate,
    canceling,
    generateAiCreationVideo,
    generateAiCreationImage,
    deleteCreationOneofRecord,
    deleteCreationOneofRecordLoading,
  };
}

/**
 * 根据新旧模型的配置，计算需要保留的参数
 * @param oldParams 当前的参数值 (例如 { aspectRatio: '9:16', duration: '8' })
 * @param oldModelKey 旧模型名称
 * @param newModelKey 新模型名称
 */
const getNextModelParams = (
  oldParams: AiCreationParams,
  oldModelKey: string,
  newModelKey: string
): AiCreationParams => {
  const oldConfig = MODEL_PARAMS_CONFIGS[oldModelKey];
  const newConfig = MODEL_PARAMS_CONFIGS[newModelKey];

  // 获取新模型的默认初始参数（从 CREATION_DEFAULT_CONFIG 中取）
  // 这里的 type 需要根据 newModelKey 判定，如果是通用函数，建议从 CREATION_DEFAULT_CONFIG 匹配
  const findTypeByModel = (modelName: string) =>
    Object.keys(CREATION_DEFAULT_CONFIG).find(type => CREATION_DEFAULT_CONFIG[type].models[modelName]) as AiCreationType;

  const newType = findTypeByModel(newModelKey);
  const defaultNewParams = { ...CREATION_DEFAULT_CONFIG[newType].models[newModelKey] };

  if (!oldConfig || !newConfig) return defaultNewParams;

  const resultParams = { ...defaultNewParams };

  // 遍历新模型拥有的字段
  newConfig.fields.forEach((field: string) => {
    // 检查旧模型是否也拥有这个字段
    if (oldConfig.fields.includes(field)) {
      const oldValue = oldParams[field];
      const newOptions = newConfig.options[field] || [];

      // 检查旧的值是否在新模型的选项列表中
      const hasValueInNewOptions = newOptions.some((opt) => opt.value === oldValue);

      if (hasValueInNewOptions && oldValue) {
        resultParams[field] = oldValue;
      }
    }
  });

  return resultParams;
};

type CosUploadSettledResult = PromiseSettledResult<
  Awaited<ReturnType<typeof cosUploadFile>>
>;

// 多文件 COS 上传：allSettled，仅聚合成功项；失败写入控制台
const cosUploadFilesAllowPartial = async (
  files: File[],
  scene: Parameters<typeof cosUploadFile>[1],
  failureLogLabel: string
): Promise<{
  successes: { url: string; size: number }[];
  results: CosUploadSettledResult[];
}> => {
  const results = await Promise.allSettled(
    files.map((f) => cosUploadFile(f, scene))
  );
  const successes: { url: string; size: number }[] = [];
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    const file = files[i];
    if (result.status === "fulfilled") {
      successes.push({ url: result.value.url, size: file.size });
    } else {
      console.error(`${failureLogLabel}:`, file?.name, result.reason);
    }
  }
  return { successes, results };
};

// ai创作相关hook
const useAiCreationData = () => {
  const { showToastByCode, toast } = useToast();
  const params = useParams<{ id: string }>();
  const agentId = params.id || 'default';

  // 统一的 Storage Key
  const STORAGE_KEY = `MIUTA_AI_CREATION_${agentId}`;

  // 获取默认值
  const getDefaultConfig = () => ({
    type: 'video' as AiCreationType,
    model: CREATION_DEFAULT_CONFIG.video.defaultModel,
    params: CREATION_DEFAULT_CONFIG.video.models[KLING_V26_PRO],
  });

  // 读取缓存
  const getInitialData = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const defaultValue = getDefaultConfig();

    if (!saved) return defaultValue;

    try {
      const data = JSON.parse(saved);

      // 确定 type
      const type: AiCreationType = data.type && CREATION_DEFAULT_CONFIG[data.type]
        ? data.type
        : defaultValue.type;

      // 确定 model
      const modelConfig = CREATION_DEFAULT_CONFIG[type];
      const model = data.model && modelConfig.models[data.model]
        ? data.model
        : modelConfig.defaultModel;

      // 合并参数
      const defaultModelParams = modelConfig.models[model];
      const cachedParams = data.params || {};

      // 缓存无数据时使用默认值
      const mergedParams: AiCreationParams = {
        aspectRatio: cachedParams.aspectRatio || defaultModelParams.aspectRatio,
        imageSize: cachedParams.imageSize || defaultModelParams.imageSize,
        count: cachedParams.count || defaultModelParams.count,
        duration: cachedParams.duration || defaultModelParams.duration,
      };

      return { type, model, params: mergedParams };
    } catch (e) {
      console.error("Parse config error:", e);
      return defaultValue;
    }
  };

  // 状态初始化
  const [aiCreationType, setAiCreationType] = useState<AiCreationType>(() => getInitialData().type);
  const [aiCreationModel, setAiCreationModel] = useState(() => getInitialData().model);
  const [aiCreationParams, setAiCreationParams] = useState<AiCreationParams>(() => getInitialData().params);
  // 创作参考图
  const [referenceImages, setReferenceImages] = useState<string[]>([]);
  // 创作参考图大小（KB）
  const [referenceImageKbs, setReferenceImageKbs] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  // 创作尾帧图
  const [endFrameImage, setEndFrameImage] = useState([]);
  const [endFrameImageLoading, setEndFrameImageLoading] = useState(false);

  // 监听 agentId 变化同步数据
  useEffect(() => {
    const data = getInitialData();
    // 切换智能体，立即同步状态为新 ID 的缓存或默认值
    setAiCreationType(data.type);
    setAiCreationModel(data.model);
    setAiCreationParams(data.params);
    setReferenceImages([]); // 切换智能体清空参考图
  }, [agentId]);

  // 联动逻辑，当创作类型改变时，更新模型和参数
  const handleTypeChange = (type: AiCreationType) => {
    const newModel = CREATION_DEFAULT_CONFIG[type].defaultModel;
    const newParams = getNextModelParams(aiCreationParams, aiCreationModel, newModel);

    setAiCreationType(type);
    handleModelChange(newModel); // 暂时处理
    setAiCreationParams(newParams);
  };

  // 监听模型变化，更新参数
  const handleModelChange = (model: string) => {
    setAiCreationModel(model);
    const newParams = getNextModelParams(aiCreationParams, aiCreationModel, model);
    setAiCreationParams(newParams);

    // 处理参考图切换逻辑
    const oldModelOption = getModelOptionByValue(aiCreationModel);
    const newModelOption = getModelOptionByValue(model);
    const newMaxImages = newModelOption?.maxImages || 1;
    const newMaxFileSize = newModelOption?.maxInputFileSize || 0;
    const oldMaxFileSize = oldModelOption?.maxInputFileSize || 0;

    // 只有在文件大小限制变化，或者当前图片数超过新模型上限时才执行过滤
    if (newMaxFileSize !== oldMaxFileSize || referenceImages.length > newMaxImages) {
      const { filteredImages, filteredKbs, isChanged } = getFilteredReferenceData(
        referenceImages,
        referenceImageKbs,
        newMaxImages,
        newMaxFileSize
      );

      if (isChanged) {
        setReferenceImages(filteredImages);
        setReferenceImageKbs(filteredKbs);
      }
    }
  };

  // 根据新模型限制，计算过滤后的参考图数据
  const getFilteredReferenceData = (
    images: string[],
    kbs: number[],
    maxImages: number,
    maxFileSize: number
  ) => {
    // 1. 基于文件大小进行过滤（利用索引确保 image 和 kb 对应）
    let filteredImages = images.filter((_, index) => {
      const fileSize = kbs[index] || 0;
      return fileSize <= maxFileSize;
    });

    // 2. 同步过滤大小数组
    let filteredKbs = kbs.filter((size) => size <= maxFileSize);

    // 3. 根据最大张数限制进行裁切
    if (filteredImages.length > maxImages) {
      filteredImages = filteredImages.slice(0, maxImages);
      filteredKbs = filteredKbs.slice(0, maxImages);
    }

    return {
      filteredImages,
      filteredKbs,
      isChanged: filteredImages.length !== images.length
    };
  };

  // 处理参考图
  const handleReferenceImagesChange = (images: string[]) => {
    if (aiCreationType === 'video') {
      setAiCreationParams({
        ...aiCreationParams,
        aspectRatio: images?.length > 0 ? '' : getDefaultConfig().params.aspectRatio,
      });
    }
    setReferenceImages(images);
  }

  // 持久化存储
  useEffect(() => {
    const configToSave = {
      type: aiCreationType,
      model: aiCreationModel,
      params: aiCreationParams,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configToSave));
  }, [STORAGE_KEY, aiCreationType, aiCreationModel, aiCreationParams]);

  // 处理上传文件
  const uploadReferenceImages = async (file: File | File[]) => {
    const fileArray = Array.isArray(file) ? file : [file];

    // 计算剩余可上传数量
    const modelOption = getModelOptionByValue(aiCreationModel);
    const maxImages = modelOption?.maxImages || 1;
    const maxUploadable = maxImages - referenceImages?.length;
    // 最终可上传文件
    const uploadableFiles = fileArray.slice(0, maxUploadable);

    setUploading(true);
    try {
      const { successes, results } = await cosUploadFilesAllowPartial(
        uploadableFiles,
        "common",
        "Reference image upload failed"
      );

      if (successes.length > 0) {
        setReferenceImages((prev) => [...prev, ...successes.map((s) => s.url)]);
        setReferenceImageKbs((prev) => [...prev, ...successes.map((s) => s.size)]);
      }

    } catch (error) {
      console.error("Upload failed:", error);
      showToastByCode(error);
    } finally {
      setUploading(false);
    }
  }

  // 处理上传尾帧图
  const handleUploadEndFrameImage = async (files: File | File[]) => {
    setEndFrameImageLoading(true);
    try {
      const fileArray = Array.isArray(files) ? files : [files];
      const { successes, results } = await cosUploadFilesAllowPartial(
        fileArray,
        "common",
        "End frame image upload failed"
      );

      if (successes.length > 0) {
        setEndFrameImage(successes.map((s) => s.url));
      }

    } catch (error) {
      console.error("Upload failed:", error);
      showToastByCode(error);
    } finally {
      setEndFrameImageLoading(false);
    }
  }

  const handleEndFrameImageChange = (images: string[]) => {
    setEndFrameImage(images);
  }

  // 首帧图和尾帧图互换
  const handleSwitchFirstAndEndFrame = () => {
    if (endFrameImage.length > 0 && referenceImages.length > 0) {
      setReferenceImages([endFrameImage[0]]);
      setEndFrameImage([referenceImages[0]]);
    }
  }

  return {
    aiCreationType,
    aiCreationModel,
    aiCreationParams,

    setAiCreationParams,
    setAiCreationModel: handleModelChange,
    setAiCreationType: handleTypeChange,
    setReferenceImages: handleReferenceImagesChange,

    referenceImages,
    uploadReferenceImagesLoading: uploading,
    uploadReferenceImages,

    endFrameImage,
    endFrameImageLoading,
    handleUploadEndFrameImage,
    handleEndFrameImageChange,
    handleSwitchFirstAndEndFrame,
  };
}

// 创作记录
const useAiCreationRecord = () => {
  const params = useParams();
  const managerRef = useRef<AgentUserMessagesManager | null>(null);

  const [aiMakeMessageRecords, setAiMakeMessageRecords] = useState<AiMakeMessageRecordData[]>([]);
  const [total, setTotal] = useState(0);
  const [imageTotal, setImageTotal] = useState(0);
  const [videoTotal, setVideoTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteCreationOneofRecordLoading, setDeleteCreationOneofRecordLoading] = useState(false);
  const [filterType, setFilterType] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    managerRef.current = new AgentUserMessagesManager(params.id);
  }, []);

  // 获取创作记录
  const getAiCreationRecords = async (type: number, page: number, pageSize: number, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const records = await managerRef.current?.getAiCreationRecords(type, page, pageSize);
      const dataList = records?.records.filter(item => item.status === 1) || [];

      // 检查是否有更多数据
      const currentTotal = records?.total || 0;
      // 基于页码和页面大小判断是否有更多数据
      const hasMoreData = page * pageSize < currentTotal;
      setHasMore(hasMoreData);

      if (append) {
        // 追加模式：合并数据，去重
        setAiMakeMessageRecords(prev => {
          const existingIds = new Set(prev.map(item => item.id));
          const newItems = dataList.filter(item => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      } else {
        // 重置模式
        setAiMakeMessageRecords(dataList);
        setPage(1);
        setHasMore(dataList.length === pageSize);
      }

      setTotal(currentTotal);
      setImageTotal(records?.imageTotal || 0);
      setVideoTotal(records?.videoTotal || 0);
    } catch (err) {
      console.error("getAiCreationRecords failed:", err.message);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }

  // 加载更多
  const loadMore = async () => {
    if (loading || loadingMore || !hasMore) return;

    const nextPage = page + 1;
    try {
      await getAiCreationRecords(filterType, nextPage, 100, true);
      setPage(nextPage);
    } catch (err) {
      console.error("loadMore failed:", err.message);
    }
  };

  // 删除创作记录
  const deleteAiCreationRecord = async (list: { id: number, type: number }[]) => {
    try {
      const ids = list.map(item => item.id);
      const imageCount = list.filter(item => item.type === 1).length;
      const videoCount = list.filter(item => item.type === 2).length;

      setDeleteCreationOneofRecordLoading(true);
      await managerRef.current?.deleteCreationOneofRecord(ids);

      // 乐观更新
      setAiMakeMessageRecords(prevRecords => prevRecords.filter(item => !ids.includes(item.id)));
      setTotal(prevTotal => prevTotal - imageCount - videoCount);
      setImageTotal(prevImageTotal => prevImageTotal - imageCount);
      setVideoTotal(prevVideoTotal => prevVideoTotal - videoCount);
    } catch (err) {
      console.error("deleteAiCreationRecord failed:", err.message);
    } finally {
      setDeleteCreationOneofRecordLoading(false);
    }
  }

  useEffect(() => {
    getAiCreationRecords(filterType, 1, 100);
  }, [filterType]);

  return {
    aiMakeMessageRecords,
    aiCreationTotal: total,
    imageTotal,
    videoTotal,
    loading,
    filterType,
    setFilterType,
    getAiCreationRecords,
    deleteAiCreationRecord,
    deleteCreationOneofRecordLoading,
    hasMore,
    loadingMore,
    loadMore,
  }
}

// 口播台词读音标记（页面级状态，与 generate 请求中的 marks 字段对应）
const useLiveScriptPronunciationMarks = () => {
  const [scriptMarks, setScriptMarks] = useState<ScriptReplaceMark[]>([]);
  const clearScriptMarks = useCallback(() => {
    setScriptMarks([]);
  }, []);
  return { scriptMarks, setScriptMarks, clearScriptMarks };
};

// 口播文案违禁词检测：调用接口并保存 riskWords，供输入框高亮与提交流程使用
const useAudioContentIllegalCheck = () => {
  const [riskWords, setRiskWords] = useState<string[]>([]);
  const [checking, setChecking] = useState(false);
  // 最近一次违禁词接口请求时的完整文案（用于「文案未变」时跳过重复请求）
  const lastCheckedTextRef = useRef<string | null>(null);

  const checkScriptIllegal = useCallback(async (inputText: string) => {
    setChecking(true);
    try {
      const data = await audioContentIllegal({ inputText });
      const words = data.riskWords ?? [];
      setRiskWords(words);
      lastCheckedTextRef.current = inputText;
      return words;
    } catch (e) {
      setRiskWords([]);
      lastCheckedTextRef.current = null;
      throw e;
    } finally {
      setChecking(false);
    }
  }, []);

  const clearScriptRiskWords = useCallback(() => {
    setRiskWords([]);
    lastCheckedTextRef.current = null;
  }, []);

  /**
   * 为 true 时可不请求接口，直接用当前 riskWords 做本地判断（runScriptIllegalSubmitGate）。
   * - 与上次检测时文案完全一致；
   * - 或当前文案仍包含上次接口返回的任一违禁词（例如只删了部分词、其余仍在 riskWords 中）。
   */
  const isScriptIllegalCheckFreshForText = useCallback((text: string) => {
    if (lastCheckedTextRef.current !== null && lastCheckedTextRef.current === text) {
      return true;
    }
    if (riskWords.length > 0 && riskWords.some((w) => w && text.includes(w))) {
      return true;
    }
    return false;
  }, [riskWords]);

  return {
    scriptRiskWords: riskWords,
    checkingScriptIllegal: checking,
    checkScriptIllegal,
    clearScriptRiskWords,
    isScriptIllegalCheckFreshForText,
  };
};

// 口播试听
const useTTSAudition = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { showToastByCode, toast } = useToast();
  const [loading, setLoading] = useState(false);
  const managerRef = useRef<AgentUserMessagesManager | null>(null);
  const [ttsCreateResult, setTtsCreateResult] = useState<TTSCreateResultData | null>(null);

  useEffect(() => {
    managerRef.current = new AgentUserMessagesManager(id);
  }, [id]);

  useEffect(() => {
    const unsubscribe = managerRef.current?.ttsListen((e) => {
      setTtsCreateResult(e.detail);
    });
    return () => unsubscribe?.();
  }, []);

  const generateAudio = useCallback(async (params: AudioTTSCreateProps) => {
    if (!params.text?.trim()) return "";
    if (!params.voiceId) {
      toast({ description: t("live_script.audio_preview_no_voice") });
      return "";
    }

    setLoading(true);
    try {
      const data = await managerRef.current?.generateTTSAudioPreview(params);
      if (!data?.fileUrl) {
        throw new Error("Audio url is empty");
      }
      // 更新积分
      subscriptionManager.fetchAgentCredits();
      return data.fileUrl;
    } catch (err) {
      showToastByCode(err);
      return "";
    } finally {
      setLoading(false);
    }
  }, [showToastByCode, toast]);

  const clearTTSCreateResult = useCallback(() => {
    setTtsCreateResult({} as TTSCreateResultData)
    managerRef.current?.setTTSCreateResult({} as TTSCreateResultData);
  }, []);

  return {
    loading,
    ttsCreateResult,
    audioUrl: ttsCreateResult?.fileUrl,
    generateAudio,
    clearTTSCreateResult,
  };
};

// 缓存的输出相关配置（短视频预估时长 / 输出语言 / 口播台词开关 / 脚本违禁词检测开关）
const useOutputConfig = (agentId?: string) => {
  agentId = agentId || agentManager.getAgentId();
  const suffix = agentId ?? "";

  // 输出语言
  const [outLang, setOutLang] = useCachedState<string>(
    suffix ? `MIUTA_OUT_LANG_${suffix}` : "",
    "zh-HK", // 默认繁体
  );

  // 短视频预估时长
  const [estimatedDuration, setEstimatedDuration] = useCachedState<EstimatedDurationState>(
    suffix ? `MIUTA_VIDEO_DURATION_${suffix}` : "",
    {
      preset: 15,
      customMinutes: 0,
      customSeconds: 15,
    },
  );

  // 口播台词开关
  const [liveStreamScript, setLiveStreamScript] = useCachedState<boolean>(
    suffix ? `MIUTA_LIVE_STREAM_SCRIPT_${suffix}` : "",
    false, // 默认关闭
  );

  // 脚本违禁词检测开关
  const [openScriptIllegal, setOpenScriptIllegal] = useCachedState<boolean>(
    suffix ? `MIUTA_OPEN_SCRIPT_ILLEGAL_${suffix}` : "",
    false, // 默认关闭
  );

  return {
    estimatedDuration,
    setEstimatedDuration,
    outLang,
    setOutLang,
    liveStreamScript,
    setLiveStreamScript,
    openScriptIllegal,
    setOpenScriptIllegal,
  };
};

export const agentUserMessagesHooks = {
  useAgentUserMessages,
  useAiCreationData,
  useAiCreationRecord,
  useLiveScriptPronunciationMarks,
  useAudioContentIllegalCheck,
  useTTSAudition,
  useOutputConfig,
};

export default AgentUserMessagesManager;